import {
  Badge,
  Box,
  Heading,
  Link,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Component to render help content with markdown support
 */
const HelpContent = ({ sections, navigateToSection, getRelatedTerms }) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const linkColor = useColorModeValue('brand.500', 'brand.400');
  const codeBlockBg = useColorModeValue('gray.100', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.800');

  if (!sections || sections.length === 0) {
    return (
      <Card p="30px">
        <Text color={textColor}>
          No help content found. Try adjusting your search.
        </Text>
      </Card>
    );
  }

  // Custom components for markdown rendering
  const markdownComponents = {
    h1: ({ children }) => (
      <Heading as="h1" size="xl" mt="8" mb="4" color={textColor}>
        {children}
      </Heading>
    ),
    h2: ({ children }) => (
      <Heading as="h2" size="lg" mt="6" mb="3" color={textColor}>
        {children}
      </Heading>
    ),
    h3: ({ children }) => (
      <Heading as="h3" size="md" mt="5" mb="2" color={textColor}>
        {children}
      </Heading>
    ),
    h4: ({ children }) => (
      <Heading as="h4" size="sm" mt="4" mb="2" color={textColor}>
        {children}
      </Heading>
    ),
    p: ({ children }) => (
      <Text mb="4" color={textColor} lineHeight="tall">
        {children}
      </Text>
    ),
    ul: ({ children }) => (
      <Box as="ul" pl="6" mb="4" color={textColor}>
        {children}
      </Box>
    ),
    ol: ({ children }) => (
      <Box as="ol" pl="6" mb="4" color={textColor}>
        {children}
      </Box>
    ),
    li: ({ children }) => (
      <Box as="li" mb="2">
        {children}
      </Box>
    ),
    a: ({ href, children }) => (
      <Link
        href={href}
        color={linkColor}
        textDecoration="underline"
        _hover={{ opacity: 0.8 }}
      >
        {children}
      </Link>
    ),
    code: ({ inline, children, ...props }) => {
      if (inline) {
        return (
          <Text
            as="code"
            bg={codeBlockBg}
            px="2"
            py="1"
            borderRadius="md"
            fontSize="sm"
            fontFamily="mono"
          >
            {children}
          </Text>
        );
      }
      return (
        <Box
          as="pre"
          bg={codeBlockBg}
          p="4"
          borderRadius="md"
          overflowX="auto"
          mb="4"
        >
          <Text as="code" fontSize="sm" fontFamily="mono" {...props}>
            {children}
          </Text>
        </Box>
      );
    },
    pre: ({ children }) => <>{children}</>,
    blockquote: ({ children }) => (
      <Box
        borderLeftWidth="4px"
        borderLeftColor={linkColor}
        pl="4"
        py="2"
        my="4"
        fontStyle="italic"
        color={textColor}
      >
        {children}
      </Box>
    ),
    table: ({ children }) => (
      <Box overflowX="auto" mb="4">
        <Box
          as="table"
          width="100%"
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
        >
          {children}
        </Box>
      </Box>
    ),
    thead: ({ children }) => (
      <Box
        as="thead"
        bg={tableHeaderBg}
        fontWeight="semibold"
      >
        {children}
      </Box>
    ),
    tbody: ({ children }) => <Box as="tbody">{children}</Box>,
    tr: ({ children }) => (
      <Box as="tr" borderBottomWidth="1px" borderColor={borderColor}>
        {children}
      </Box>
    ),
    th: ({ children }) => (
      <Box as="th" px="4" py="3" textAlign="left" color={textColor}>
        {children}
      </Box>
    ),
    td: ({ children }) => (
      <Box as="td" px="4" py="3" color={textColor}>
        {children}
      </Box>
    ),
  };

  return (
    <VStack spacing="6" align="stretch">
      {sections.map((section) => (
        <Card
          key={section.id}
          id={section.id}
          data-section-id={section.id}
          p="30px"
        >
          {/* Section Header */}
          <Box mb="4">
            {section.parentTitle && (
              <Text
                fontSize="sm"
                color="secondaryGray.600"
                mb="2"
                fontWeight="500"
              >
                {section.parentTitle}
              </Text>
            )}
            <Heading as="h1" size="xl" color={textColor} mb="2">
              {section.title}
              {section.fullTitle && (
                <Text
                  as="span"
                  fontSize="lg"
                  color="secondaryGray.600"
                  ml="3"
                  fontWeight="normal"
                >
                  ({section.fullTitle})
                </Text>
              )}
            </Heading>
            {section.type && (
              <Badge
                colorScheme={section.type === 'guide' ? 'blue' : 'green'}
                fontSize="xs"
              >
                {section.type === 'guide' ? 'Page Guide' : 'Lexicon'}
              </Badge>
            )}
          </Box>

          {/* Markdown Content */}
          <Box>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {section.content}
            </ReactMarkdown>
          </Box>

          {/* Related Terms for Lexicon entries */}
          {section.type === 'lexicon' && section.relatedTerms && (
            <Box mt="6" pt="6" borderTopWidth="1px" borderColor={borderColor}>
              <Text
                fontSize="sm"
                fontWeight="600"
                color={textColor}
                mb="3"
              >
                Related Terms:
              </Text>
              <Box display="flex" flexWrap="wrap" gap="2">
                {getRelatedTerms(section.id).map((relatedTerm) => (
                  <Badge
                    key={relatedTerm.id}
                    colorScheme="purple"
                    px="3"
                    py="1"
                    borderRadius="md"
                    cursor="pointer"
                    onClick={() => navigateToSection(relatedTerm.id)}
                    _hover={{ opacity: 0.8 }}
                  >
                    {relatedTerm.term}
                  </Badge>
                ))}
              </Box>
            </Box>
          )}
        </Card>
      ))}
    </VStack>
  );
};

export default HelpContent;
